import type {FinishedSaveResponse, IdentifiedFile, ViewableFile} from 'typings/portaro.be.types';
import type {FileDataService} from './file.data-service';
import type {FileContentDataService} from './file-content.data-service';
import type {FileUploadService} from './file-upload.service';
import {exists} from 'shared/utils/custom-utils';

export class FileService {
    public static serviceName = 'fileService';

    private readonly filesByDirectoryCache = new Map<number, ViewableFile[]>();

    /*@ngInject*/
    constructor(private fileDataService: FileDataService,
                private fileContentDataService: FileContentDataService,
                private fileUploadService: FileUploadService) {
    }

    public async getFileById(id: number): Promise<ViewableFile> {
        let file = await this.fileDataService.getFile(id);

        file = this.extendFile(file);

        return file
    }

    public async getFilesByDirectory(directoryId: number, excludedFileIds?: number[]): Promise<ViewableFile[]> {
        const cached = this.filesByDirectoryCache.get(directoryId);
        if (exists(cached)) {
            return cached;
        }

        let files = await this.fileDataService.queryFiles({
            directory: [directoryId],
            excludedFile: excludedFileIds
        });

        files = files.map((file) => this.extendFile(file));

        this.filesByDirectoryCache.set(directoryId, files);

        return files;
    }

    public async editFileMetadata(file?: IdentifiedFile): Promise<FinishedSaveResponse<ViewableFile>> {
        const response = await this.fileDataService.editFileMetadata({...file, confirmed: false});

        if (exists(file?.directory)) {
            this.filesByDirectoryCache.delete(file.directory.id);
        }

        return response;
    }

    public saveFileContent(file: IdentifiedFile, content: string): Promise<IdentifiedFile> {
        return this.fileDataService.modifyFileContent(file, content);
    }

    public async uploadFile(directoryId: number, file: File, progressCallback?: (value: number) => void): Promise<ViewableFile> {
        const response = await this.fileUploadService.upload('/api/files/upload', file, {directory: directoryId}, progressCallback);

        this.filesByDirectoryCache.delete(directoryId);

        return response;
    }

    public async createNewFile(directoryId?: number, filename?: string): Promise<ViewableFile> {
        const saveResponse = await this.fileDataService.createFile(directoryId, filename);

        const file = this.extendFile(saveResponse.savedObject);

        this.filesByDirectoryCache.delete(directoryId);

        return file;
    }

    public async deleteFile(file: IdentifiedFile): Promise<void> {
        await this.fileDataService.removeFile(file);

        if (exists(file.directory)) {
            this.filesByDirectoryCache.delete(file.directory.id);
        }
    }

    public getFileContent(file: IdentifiedFile): Promise<string> {
        return this.fileContentDataService.getFileContent(file);
    }

    public getFilePath(file: IdentifiedFile): string {
        return `/files/${file.id}`;
    }

    private extendFile<T extends IdentifiedFile>(file: T): T {
        file.url = this.getFilePath(file);
        return file;
    }
}